import { useState } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import OutOfCoinsAmountWrapperPng from '../../assets/out-of-coins/out_of_coins_popup_amount_wrapper.png';
import OutOfCoinsAmountWrapperWebp from '../../assets/out-of-coins/out_of_coins_popup_amount_wrapper.webp';
import { useCoinsAnimation } from '../../hooks/useCoinsAnimation';
import type { ClaimableReward } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';
import { formatValueWithK } from '../../utils/helpers';
import { devError } from '../../utils/log';
import Button from '../buttons/Button';
import { SocialCoin } from '../SocialCoin/SocialCoin';
import CloseIconBold from '../SVGIcons/CloseIconBold';

import styles from './OutOfCoins.module.scss';

const OutOfCoins = observer(() => {
  const { portalStore, gameStore, lowBalanceStore } = useStores();
  const [isClaiming, setIsClaiming] = useState(false);
  const { t } = useTranslation();
  const { startAnimation } = useCoinsAnimation();

  const freeCoinsAmount = lowBalanceStore.thresholdPrize || 0;

  const onClickClose = () => {
    portalStore.closePortal();
    lowBalanceStore.reset();
  };

  const onClickClaim = async () => {
    if (isClaiming) {
      return;
    }

    try {
      setIsClaiming(true);
      const claimed = await lowBalanceStore.claimRefillBonus();
      if (claimed) {
        gameStore.updateBalanceInGame();

        const rewards: ClaimableReward[] = [{ scCoin: 0, gcCoin: freeCoinsAmount, bundleItems: [] }];

        startAnimation({
          rewards,
          onEnd: () => {
            portalStore.closePortal();
          },
        });
      } else {
        setIsClaiming(false);
        portalStore.closePortal();
      }
    } catch (error) {
      devError('Claim refill bonus error:', error);
      setIsClaiming(false);
    } finally {
      lowBalanceStore.reset();
    }
  };

  return (
    <div className={styles.outOfCoinsWrapper} data-testid="low-gc-amount-frame">
      <CloseIconBold className={styles.closeIcon} onClick={onClickClose} />
      <h1 className={styles.title}>{t('coins.outOfCoins')}</h1>
      <p className={styles.paragraph} data-testid="out-of-coins-grab-free">
        <Trans components={{ free: <span className={styles.freeCC} /> }} i18nKey="coins.grabYourFreeCrownCoins" />
      </p>
      <p className={styles.paragraph}>{t('coins.andKeepSpinning')}</p>
      <div className={styles.amountWrapper}>
        <picture>
          <source srcSet={OutOfCoinsAmountWrapperWebp} type="image/webp" />
          <source srcSet={OutOfCoinsAmountWrapperPng} type="image/png" />
          <img src={OutOfCoinsAmountWrapperPng} />
        </picture>
        <span className={styles.amount}>
          <SocialCoin className={styles.goldCoin} style={{ width: 26 }} />
          {formatValueWithK(freeCoinsAmount, t)}
        </span>
      </div>
      <Button
        btnType="main"
        classNameWrapper={styles.claimBtn}
        dataTestId="low-gc-amount-claim-btn"
        disabled={isClaiming}
        onClick={onClickClaim}
      >
        {t('common.buttons.claim')}
      </Button>
    </div>
  );
});

export default OutOfCoins;
